
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ProductLimitCellProps } from "./types";
import { useQueryClient } from "@tanstack/react-query";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { toast } from "sonner";

export const ProductLimitCell = ({ 
  userId, 
  subscriptionPlan, 
  currentLimit,
  onUpdateLimit 
}: ProductLimitCellProps) => {
  const [editingLimit, setEditingLimit] = useState<number | null>(null);
  const queryClient = useQueryClient();

  // Fetch custom limit for this user
  const { data: customLimit } = useQuery({
    queryKey: ['customLimit', userId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('custom_product_limits')
        .select('product_limit')
        .eq('owner_id', userId)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error("Error fetching custom limit:", error);
        return null;
      }

      return data?.product_limit;
    }
  });

  // Subscribe to real-time changes
  useEffect(() => {
    const channel = supabase
      .channel('custom-limits-changes')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'custom_product_limits',
          filter: `owner_id=eq.${userId}`
        },
        (payload) => {
          console.log("Received real-time update:", payload);
          // Invalider les caches pour forcer le rechargement
          queryClient.invalidateQueries({ queryKey: ['customLimit', userId] });
          queryClient.invalidateQueries({ queryKey: ['customLimits'] });
          queryClient.invalidateQueries({ queryKey: ['subscription'] });
          toast.success("Limite de produits mise à jour");
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [userId, queryClient]);

  // Use custom limit if available, otherwise use subscription limit
  const effectiveLimit = customLimit !== null ? customLimit : currentLimit;

  const handleSave = async () => {
    if (editingLimit !== null) {
      try {
        await onUpdateLimit(userId, editingLimit);
        setEditingLimit(null);
      } catch (error) {
        console.error("Error updating limit:", error);
        toast.error("Erreur lors de la mise à jour de la limite");
      }
    }
  };

  if (editingLimit !== null) {
    return (
      <div className="flex items-center gap-2">
        <Input
          type="number"
          value={editingLimit}
          onChange={(e) => setEditingLimit(parseInt(e.target.value))}
          className="w-20"
        />
        <Button
          variant="outline"
          size="sm"
          onClick={handleSave}
        >
          Valider
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditingLimit(null)}
        >
          Annuler
        </Button>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-2">
      <span>{effectiveLimit === -1 ? "Illimité" : effectiveLimit}</span>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => setEditingLimit(effectiveLimit === -1 ? 3 : effectiveLimit)}
      >
        Modifier
      </Button>
    </div>
  );
};
